import {useState} from "react";
import SurveyVisualization from "./SurveyVisualization";
import PatientInfo from "./PatientInfo";
import {collection, getFirestore, orderBy, query, limit} from "firebase/firestore";
import {useCollectionDataOnce} from "react-firebase-hooks/firestore";
import ViewReports from "./ViewReports";
import CalibrationView from "./CalibrationView";
import GazeHeatmapView from "./GazeHeatmapView";
import LegacyContactNotes from "./LegacyContactNotes";
import LegacyEvalReport from "./report/LegacyEvalReport";
import { useMaster } from "../shared/hooks/evals";
import { makeChartData } from "../shared/data/charting";
import EvalDisplayShell from "../shared/eval-display/EvalDisplayShell";
import EvalGraphsDisplay from "../shared/eval-display/EvalGraphsDisplay";

const tabs = [
    { name: 'Normalized', href: '#', current: true },
    { name: 'Raw', href: '#', current: false },
    { name: 'Self-Report', href: '#', current: false},
    { name: 'Patient Info', href: '#', current: false},
    { name: 'Notes', href: '#', current: false},
    { name: 'View Reports', href: '#', current: false},
    { name: 'Report', href: '#', current: false},
    { name: 'Calibration', href: '#', current: false},
    { name: 'Gaze Heatmap', href: '#', current: false}
]

export default function EvalGraphs({selectedCase, selectedEvalId, user}) {
    // Load the master from the database.
    const master = useMaster(selectedCase['eval_data']['client']['Sex']);
    const [graphType, setGraphType] = useState('Normalized');
    const collectionRef = collection(getFirestore(), 'report_templates');
    const [
        values,
        loading,
        error,
        snapshot
    ] = useCollectionDataOnce(query(collectionRef, orderBy("version", "desc"), limit(1)));

    function makeTitle(selectedCase) {
        return `${selectedCase.eval_data.client.name} - ${selectedCase.eval_data.case_num} - ${selectedCase.eval_data.date}`;
    }

    // Get our raw and normalized data.
    const [rawData, normalizedData] = master === null ? [null, null] : makeChartData({
        selectedCase: selectedCase,
        master: master
    });

    function getWidget(graphType) {
        switch (graphType) {
            case 'Normalized':
                return master === null ? <p>Loading charts...</p> : <EvalGraphsDisplay data={normalizedData} />
            case 'Raw':
                return master === null ? <p>Loading charts...</p> : <EvalGraphsDisplay data={rawData} />
            case 'Self-Report':
                return <SurveyVisualization data={selectedCase.post_test_survey} />
            case 'Patient Info':
                return <PatientInfo data={selectedCase.eval_data.client} />
            case 'Notes':
                return <LegacyContactNotes key={selectedEvalId} evalId={selectedEvalId} />
            case 'View Reports':
                return <ViewReports evalId={selectedEvalId} />
            case 'Report':
                if (master === null) return <p>Loading charts...</p>;
                return (
                    <LegacyEvalReport
                        master={master}
                        normalizedData={normalizedData}
                        rawData={rawData}
                        selectedCase={selectedCase}
                        selectedEvalId={selectedEvalId}
                    />
                )
            case 'Calibration':
                return <CalibrationView evalId={selectedEvalId} />
            case 'Gaze Heatmap':
                return <GazeHeatmapView evalId={selectedEvalId} />
            default:
                return <EvalGraphsDisplay data={normalizedData} />
        }
    }

    return (
        <EvalDisplayShell
            tabs={tabs}
            currentTab={graphType}
            setCurrentTab={setGraphType}
            title={makeTitle(selectedCase)}
            showResultsForm={graphType !== 'View Reports' && graphType !== 'Report' && graphType !== 'Notes'}
            submissionMeta={{
                evalId: selectedEvalId,
                caseNum: selectedCase?.eval_data?.case_num,
                scorer: user?.uid,
                source: 'legacy_eval'
            }}
        >
            {getWidget(graphType)}
        </EvalDisplayShell>
    );
}
